// shared/src/hydraulic/scenarios/closure.ts
import { isFieldOverridable } from './schema'
import type { Scenarios } from './schema'

/**
 * Structural slice of HydraulicSystem that this validator reads. We deliberately
 * DO NOT import `HydraulicSystem` here: HydraulicSystemSchema's `.refine()` calls
 * this function, and `HydraulicSystem = z.infer<typeof HydraulicSystemSchema>`, so
 * naming that type in this parameter would make the schema's inferred type
 * reference itself (TS2502 "referenced directly or indirectly in its own type
 * annotation"). A structural input type breaks the cycle while still accepting a
 * full HydraulicSystem at the call site.
 */
export interface ScenarioClosureInput {
  scenarios?: Scenarios
  vertices: ReadonlyArray<{ id: string; type: string }>
  edges: ReadonlyArray<{ id: string; type: string }>
  pipe_specifications: ReadonlyArray<{ id: string; sizes?: ReadonlyArray<{ id: string }> }>
}

/**
 * System-context validation for `system.scenarios` (mirrors
 * device-library/validateSnapshotClosure). Returns human-readable violations
 * (empty = valid). Intra-tree rules (active_id ∈ nodes, acyclic, unique ids)
 * are enforced by ScenariosSchema; this checks references INTO the system:
 *  - each override target id resolves to a vertex/edge
 *  - the override field is allowed for that element's type (incl. duct excl.)
 *  - a pipe_spec override's (spec, size) exists in shared pipe_specifications
 *
 * Typed loosely: scenarios may be present on a parsed or in-flight system.
 */
export function validateScenariosClosure(system: ScenarioClosureInput): string[] {
  const scenarios = system.scenarios
  if (!scenarios) return []

  const errs: string[] = []
  const vertexType = new Map(system.vertices.map((v) => [v.id, v.type]))
  const edgeType = new Map(system.edges.map((e) => [e.id, e.type]))
  const specSizes = new Map(
    system.pipe_specifications.map((s) => [s.id, new Set((s.sizes ?? []).map((sz) => sz.id))]),
  )

  for (const node of scenarios.nodes) {
    for (const ov of node.overrides) {
      const elementType = ov.target === 'vertex' ? vertexType.get(ov.id) : edgeType.get(ov.id)
      if (elementType === undefined) {
        errs.push(`Scenario '${node.id}' overrides ${ov.target} '${ov.id}', which does not exist`)
        continue
      }
      if (!isFieldOverridable(elementType, ov.field)) {
        errs.push(`Scenario '${node.id}': field '${ov.field}' is not overridable on ${elementType} '${ov.id}'`)
        continue
      }
      if (ov.field === 'pipe_spec') {
        const sizes = specSizes.get(ov.value.pipe_specification_id)
        if (!sizes) {
          errs.push(`Scenario '${node.id}': pipe spec '${ov.value.pipe_specification_id}' (edge '${ov.id}') is not in pipe_specifications`)
        } else if (!sizes.has(ov.value.pipe_size_id)) {
          errs.push(`Scenario '${node.id}': pipe size '${ov.value.pipe_size_id}' is not in spec '${ov.value.pipe_specification_id}' (edge '${ov.id}')`)
        }
      }
    }
  }
  return errs
}
